import {TriangleDownIcon} from '@primer/octicons-react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import {useRef, useState} from 'react'

import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'
import {isPullRequestOpenState} from '../../utils/pull-request-state'
import styles from './ReviewMenuButton.module.css'

import {usePendingReviews} from '../../stores/selectors'
import {ReviewMenu, type ReviewMenuOverlayMode, type ReviewMenuProps} from '../review-menu/ReviewMenu'
import {getAllowedReviewMode} from '../review-menu/get-allowed-review-mode'

export interface ReviewMenuButtonProps extends Omit<
  ReviewMenuProps,
  'overlayMode' | 'onOverlayModeChange' | 'anchorRef'
> {
  size?: 'small' | 'medium'
  className?: string
}

export function ReviewMenuButton({size = 'small', className, ...menuProps}: ReviewMenuButtonProps) {
  const {pullRequest, currentUserLogin} = menuProps
  const [overlayMode, setOverlayMode] = useState<ReviewMenuOverlayMode>('closed')

  const {author, state, viewerAllowedNonCommentReviewTypes} = pullRequest

  const pendingCommentData = usePendingReviews()

  const totalPendingComments = pendingCommentData.pendingReviewIDs?.length ?? 0

  // We will probably want to use CurrentUserContext and useCurrentUser
  const viewerIsAuthor = author?.login === currentUserLogin
  const isPROpen = isPullRequestOpenState(state)

  const anchorRef = useRef<HTMLButtonElement>(null)

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const allowedReviewMode = getAllowedReviewMode({
    isPROpen,
    viewerAllowedNonCommentReviewTypes,
    viewerIsAuthor,
  })
  const allowedReviewModeCapitalized = allowedReviewMode.charAt(0).toUpperCase() + allowedReviewMode.slice(1)

  const handleToggleReviewDialog = () => {
    if (overlayMode === 'closed') {
      // on narrow screens, the dialog is a better experience than the anchored overlay
      if (window.innerWidth < 768) {
        setOverlayMode('dialog')
      } else {
        setOverlayMode('anchored')
      }
      sendPullRequestAnalyticsEvent('submit_review_dialog.open', 'REVIEW_CHANGES_BUTTON')
    } else {
      setOverlayMode('closed')
    }
  }

  return (
    <>
      <Button
        count={totalPendingComments ? totalPendingComments : undefined}
        className={clsx(styles.ReviewMenuButton, className)}
        trailingAction={TriangleDownIcon}
        variant="primary"
        onClick={handleToggleReviewDialog}
        size={size}
        ref={anchorRef}
      >
        <span className={styles.submitTextFull}>Submit {allowedReviewMode}</span>
        <span className={styles.submitTextShort}>{allowedReviewModeCapitalized}</span>
      </Button>

      <ReviewMenu {...menuProps} anchorRef={anchorRef} overlayMode={overlayMode} onOverlayModeChange={setOverlayMode} />
    </>
  )
}
